/**
 * expertProfileService
 *
 * Orchestrates the LLM-driven update of real users' expertise profiles:
 *
 * 1. Read the report's human authors (excludes AI Assistant)
 * 2. Query all approved FINTELs where the author contributed (via authorUids)
 * 3. Aggregate authorBlocks text from each FINTEL
 * 4. POST to /api/experts/generate-profiles
 * 5. Write updated bio/expertiseKeywords back onto the real user's own profile
 *    (ticket 02) — no gate on a pre-existing Expert handle; every human author is
 *    keyed by their own uid throughout.
 *
 * Both forward (approval) and backward (rollback) triggers call this.
 */
import {getFintelsByAuthorUid} from '../../firebase/firestore/reports';
import {getUserDoc, updateExpertiseProfile} from '../../firebase/firestore/users';
import {API_BASE} from '../../utils/api/reports';
import buildHeaders from '../../utils/api/fetchHeaders';
import {storeAuthorBlocks} from './storeAuthorBlocks';

const AI_UID = 'ai';

function expertProfileDebug(label, data) {
    try {
        if (typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG_EXPERT_PROFILE') === '1') {
            console.log(`[expertProfile:debug] ${label}`, data);
        }
    } catch (_) {
        /* ignore */
    }
}

function charCountInBlocks(blocks) {
    if (!Array.isArray(blocks)) return 0;
    return blocks.reduce((n, b) => n + (typeof b === 'string' ? b.length : 0), 0);
}

/**
 * Merge authorBlocks from several approved FINTELs for one uid.
 * Sorts by approximate volume (chars) descending so the flat `data` array leads with the analyst's heaviest product,
 * reducing LLM bias toward whichever FINTEL happened to be appended first (e.g. the one just approved).
 */
function mergeAuthorBlocksForUid(authorUid, primingReport, usePriming, fintels) {
    const sources = [];

    if (usePriming) {
        const blocks = primingReport.authorBlocks?.[authorUid];
        if (Array.isArray(blocks) && blocks.length > 0) {
            sources.push({
                reportId: primingReport.id,
                updatedAt: primingReport.updatedAt,
                blocks: [...blocks],
            });
        }
    }

    for (const fintel of fintels) {
        if (usePriming && fintel.id === primingReport.id) {
            continue;
        }
        const blocks = fintel.authorBlocks?.[authorUid];
        if (Array.isArray(blocks) && blocks.length > 0) {
            sources.push({
                reportId: fintel.id,
                updatedAt: fintel.updatedAt,
                blocks: [...blocks],
            });
        }
    }

    const byReport = new Map();
    for (const s of sources) {
        if (!byReport.has(s.reportId)) {
            byReport.set(s.reportId, s);
        }
    }
    const unique = [...byReport.values()];
    unique.sort((a, b) => charCountInBlocks(b.blocks) - charCountInBlocks(a.blocks));

    /** Helps the LLM treat `data` as multiple products so "summary" is not written from the first chunk only. */
    const MULTI_PRODUCT_BOUNDARY =
        '[Boundary: more text from another approved FINTEL by this analyst follows. ' +
        'For the expertise summary, combine themes from both before and after this line—' +
        'proportional to total evidence—not only from the preceding segment.]';

    const textBlocks = [];
    for (let i = 0; i < unique.length; i++) {
        if (i > 0) {
            textBlocks.push(MULTI_PRODUCT_BOUNDARY);
        }
        textBlocks.push(...unique[i].blocks);
    }

    const fintelIdsWithBlocks = new Set(unique.map((s) => s.reportId));
    const queryIds = fintels.map((f) => f.id);
    const inQueryNoBlocks = queryIds.filter((id) => {
        if (usePriming && id === primingReport.id) {
            return false;
        }
        return !fintelIdsWithBlocks.has(id);
    });

    return {
        textBlocks,
        perProductStats: unique.map((s) => ({
            reportId: s.reportId,
            blockCount: s.blocks.length,
            approxChars: charCountInBlocks(s.blocks),
        })),
        inQueryNoBlocks,
    };
}

/**
 * After a report becomes an approved FINTEL: best-effort `authorBlocks` extraction, then LLM profile regeneration.
 * Profile refresh always runs (even if `snapshotPath` is missing or block storage fails) so experts stay in sync.
 *
 * @param {string} reportId
 * @param {string|null|undefined} snapshotPath - `report.snapshotPath` for block extraction
 */
export function scheduleExpertProfilePipelineAfterFintelApproval(reportId, snapshotPath) {
    if (!reportId) return;
    expertProfileDebug('schedule approval pipeline', {reportId, snapshotPath: snapshotPath || '(none)'});
    void storeAuthorBlocks(reportId, snapshotPath)
        .catch((err) => console.error('[expertProfileService] storeAuthorBlocks failed:', err))
        .finally(() => {
            triggerExpertProfileUpdate(reportId).catch((err) =>
                console.error('[expertProfileService] triggerExpertProfileUpdate failed:', err)
            );
        });
}

/**
 * Ticket 02: keep only the authors who are real users flagged as analysts (the existing
 * `is_analyst`/`analyst`-role signal, no new tag) — the pipeline never wrote profiles for
 * authors who lacked an Expert handle, and now the equivalent gate is "is this an analyst".
 */
async function filterToAnalystAuthors(humanAuthors) {
    const withProfiles = await Promise.all(
        humanAuthors.map(async (author) => {
            let profile = null;
            try {
                profile = await getUserDoc(author.uid);
            } catch (err) {
                console.error(`[expertProfileService] Failed to load user profile for ${author.uid}:`, err);
            }
            return {...author, isAnalyst: Boolean(profile?.isAnalyst)};
        })
    );
    return withProfiles.filter((a) => a.isAnalyst);
}

/**
 * Trigger a profile update for all human authors of the given report.
 *
 * @param {string} reportId - Firestore report document ID
 */
export async function triggerExpertProfileUpdate(reportId) {
    if (!reportId) return;

    // 1. Read the report doc to get meta.authors
    const _awpBase = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';
    const _t = typeof localStorage !== 'undefined' ? localStorage.getItem('access_token') : null;
    const _headers = _t ? {Authorization: `Bearer ${_t}`} : {};
    const reportRes = await fetch(`${_awpBase}/api/reports/${encodeURIComponent(reportId)}`, {headers: _headers});
    if (!reportRes.ok) return;
    const reportData = await reportRes.json();
    const authors = (reportData?.meta?.authors || []).filter(
        (a) => a?.uid && a.uid !== AI_UID && !a.isAI
    );

    if (authors.length === 0) {
        expertProfileDebug('trigger: no human meta.authors', {reportId});
        return;
    }

    await triggerExpertProfileUpdateForAuthors(authors, {primingReportId: reportId});
}

/**
 * Trigger a profile update for a list of author objects (uid + displayName).
 * Called on rollback when the report doc is already reset (authors list captured before reset).
 *
 * @param {Array<{uid: string, displayName: string}>} authors
 * @param {{primingReportId?: string}} [options] - If set, merge authorBlocks from this doc first so a just-approved FINTEL is included even when the indexed query lags.
 */
export async function triggerExpertProfileUpdateForAuthors(authors, options = {}) {
    if (!authors || authors.length === 0) return;

    const {primingReportId} = options;

    const humanAuthors = authors.filter(
        (a) => a?.uid && a.uid !== AI_UID && !a.isAI
    );
    if (humanAuthors.length === 0) return;

    let primingReport = null;
    if (primingReportId) {
        const _awpBase2 = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';
        const _t2 = typeof localStorage !== 'undefined' ? localStorage.getItem('access_token') : null;
        const _h2 = _t2 ? {Authorization: `Bearer ${_t2}`} : {};
        const snap = await fetch(`${_awpBase2}/api/reports/${encodeURIComponent(primingReportId)}`, {headers: _h2});
        if (snap.ok) {
            const d = await snap.json();
            primingReport = {id: primingReportId, ...d};
        }
    }

    const usePriming =
        primingReport &&
        primingReport.status === 'approved' &&
        primingReport.productType?.value !== 'disclosure';

    expertProfileDebug('authors + priming', {
        authorCount: humanAuthors.length,
        primingReportId: primingReportId || null,
        primingStatus: primingReport?.status,
        usePriming,
        primingBlockKeys: usePriming && primingReport.authorBlocks ? Object.keys(primingReport.authorBlocks) : [],
    });

    // 2. Keep only analyst-flagged real users (ticket 02: the existing analyst signal, not
    // gated behind having a pre-existing Expert handle — every author is keyed by their own uid).
    const analystAuthors = await filterToAnalystAuthors(humanAuthors);
    if (analystAuthors.length === 0) {
        expertProfileDebug('trigger: no analyst-flagged authors', {reportId: primingReportId});
        return;
    }

    // 3 + 4. For each analyst author, get all FINTELs and aggregate their authorBlocks
    const authorInputs = await Promise.all(
        analystAuthors.map(async (author) => {
            const fintels = await getFintelsByAuthorUid(author.uid);
            const {textBlocks, perProductStats, inQueryNoBlocks} = mergeAuthorBlocksForUid(
                author.uid,
                primingReport,
                usePriming,
                fintels
            );

            expertProfileDebug('aggregated blocks for author', {
                uid: author.uid,
                fintelQueryCount: fintels.length,
                totalBlocks: textBlocks.length,
                totalApproxChars: charCountInBlocks(textBlocks),
                perProductStats,
                /** Approved FINTELs returned by query but with no authorBlocks for this uid — profile ignores them until blocks exist */
                queryHitNoAuthorBlocksReportIds: inQueryNoBlocks,
            });

            return {
                author: author.displayName || author.uid,
                id: author.uid,
                data: textBlocks,
            };
        })
    );

    // Skip authors with no content
    const nonEmpty = authorInputs.filter((a) => a.data.length > 0);
    if (nonEmpty.length === 0) {
        // Still update profiles with empty data if authors exist (clears stale profiles)
        if (authorInputs.length === 0) return;
    }

    const payload = {authors: nonEmpty.length > 0 ? nonEmpty : authorInputs};

    expertProfileDebug('POST /experts/generate-profiles payload summary', {
        authorUids: payload.authors.map((a) => a.id),
        totalBlocks: payload.authors.reduce((n, a) => n + (a.data?.length || 0), 0),
        approxCharsByAuthor: payload.authors.map((a) => ({
            id: a.id,
            chars: charCountInBlocks(a.data),
        })),
    });

    // 5. POST to backend LLM endpoint
    let responseData;
    try {
        const resp = await fetch(`${API_BASE}/experts/generate-profiles`, {
            method: 'POST',
            headers: buildHeaders({'Content-Type': 'application/json'}),
            body: JSON.stringify(payload),
        });
        if (!resp.ok) {
            const text = await resp.text().catch(() => '');
            throw new Error(`Backend responded ${resp.status}: ${text}`);
        }
        responseData = await resp.json();
    } catch (err) {
        console.error('[expertProfileService] LLM call failed:', err);
        return;
    }

    if (!responseData?.experts?.length) {
        expertProfileDebug('LLM response had no experts; skipping user profile writes', {
            keys: responseData && typeof responseData === 'object' ? Object.keys(responseData) : [],
        });
        return;
    }

    const {timestamp, experts} = responseData;
    expertProfileDebug('LLM returned experts', {count: experts.length, ids: experts.map((e) => e.id)});
    const expertiseUpdatedAt = timestamp ? new Date(timestamp) : new Date();

    // 6. Write results onto each real user's own bio/expertiseKeywords fields (ticket 02).
    await Promise.all(
        experts.map(async ({id: userId, summary, specialties}) => {
            if (!userId) return;
            try {
                await updateExpertiseProfile(userId, {
                    bio: summary,
                    expertiseKeywords: specialties,
                    expertiseUpdatedAt,
                });
            } catch (err) {
                console.error(`[expertProfileService] Failed to update user ${userId}:`, err);
            }
        })
    );
}
